import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Download, Gauge, ImageUp, Layers, ShieldCheck, Sparkles } from "lucide-react";
import { Logo } from "@/components/app/TopNav";
import { PrivacyDialog } from "@/components/app/Dialogs";

const HERO = "https://images.pexels.com/photos/20688553/pexels-photo-20688553.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Feature = ({ icon: Icon, title, text, className = "", delay = 0 }) => (
  <div className={`panel p-7 rise transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#ff5a00]/40 ${className}`} style={{ animationDelay: `${delay}ms` }}>
    <Icon className="text-[#ff5a00]" size={20} />
    <h3 className="font-display text-xl font-bold mt-6 tracking-tight">{title}</h3>
    <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{text}</p>
  </div>
);

export default function Landing() {
  const nav = useNavigate();
  const [privacy, setPrivacy] = useState(false);

  return (
    <div className="min-h-screen bg-[#0d0d0e] text-white">
      <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-6 lg:px-12 bg-[#0d0d0e]/90 backdrop-blur-xl border-b border-white/5">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full border border-[#ff5a00]/40 text-[#ff7a33] bg-[#ff5a00]/10">Free plan</span>
          <button onClick={() => nav("/app")} data-testid="nav-open-app-btn" className="text-sm px-4 h-9 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors">Open workspace</button>
        </div>
      </header>

      <section className="relative overflow-hidden grain">
        <div className="grid lg:grid-cols-12 gap-10 px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
          <div className="lg:col-span-7 relative z-10">
            <p className="eyebrow rise">Image → Mesh → STL · No signup</p>
            <h1 className="font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 rise" style={{ animationDelay: "80ms" }} data-testid="landing-headline">
              Turn Any Image Into a <span className="text-[#ff5a00]">3D Printable</span> Model
            </h1>
            <p className="text-zinc-400 text-base md:text-lg mt-7 max-w-xl rise" style={{ animationDelay: "160ms" }} data-testid="landing-subtitle">
              Upload an image, create a 3D model, and download your STL for free.
            </p>
            <div className="flex flex-wrap gap-3 mt-10 rise" style={{ animationDelay: "240ms" }}>
              <button onClick={() => nav("/app")} data-testid="cta-create-model-btn" className="group h-12 px-7 rounded-full bg-[#ff5a00] hover:bg-[#ff7a33] active:bg-[#e65100] font-semibold flex items-center gap-2 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:ring-[#ff5a00]">
                Create Your 3D Model <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button onClick={() => nav("/app?sample=logo")} data-testid="cta-try-sample-btn" className="h-12 px-7 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a00]">
                Try a Sample Image
              </button>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-12 text-xs text-zinc-500 rise" style={{ animationDelay: "320ms" }}>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-zinc-400" /> No credit card</span>
              <span className="flex items-center gap-2"><Download size={14} className="text-zinc-400" /> Free STL, no watermark</span>
              <span className="flex items-center gap-2"><Gauge size={14} className="text-zinc-400" /> Relief in seconds</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative rise" style={{ animationDelay: "200ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] lg:aspect-auto lg:h-full min-h-[380px]">
              <img src={HERO} alt="3D printer printing an orange part" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0e] via-transparent to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 rounded-2xl bg-black/60 backdrop-blur-xl border border-white/10 p-4 flex items-center justify-between">
                <div>
                  <p className="eyebrow">Output</p>
                  <p className="font-mono text-sm mt-1">relief.stl · watertight · 100×100×5 mm</p>
                </div>
                <Download size={18} className="text-[#ff5a00]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 pb-24">
        <p className="eyebrow mb-8">Two ways to build</p>
        <div className="grid md:grid-cols-6 gap-6">
          <Feature className="md:col-span-4" icon={Layers} title="Relief mode — free & local" text="Logos, text, silhouettes and photos become extruded or embossed reliefs. Tune depth, base, resolution and smoothing; add a mounting hole. Always-watertight meshes generated on our server with open-source tools." />
          <Feature className="md:col-span-2" icon={Sparkles} delay={80} title="AI Full 3D" text="Reconstruct a volumetric object from a single image with TRELLIS via fal.ai. Real cloud inference with live job status." />
          <Feature className="md:col-span-2" icon={ImageUp} delay={160} title="Drag, drop, done" text="PNG, JPG, JPEG or WEBP up to 10 MB. Validation and clear errors on every step." />
          <Feature className="md:col-span-4" icon={ShieldCheck} delay={240} title="Printable, checked, private" text="Every export is checked for open boundaries, degenerate triangles and inverted normals. Files auto-delete after 24 hours and are never used for training." />
        </div>
      </section>

      <footer className="px-6 lg:px-12 py-8 border-t border-white/5 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
        <span>© Image2STL AI · Free STL downloads, forever.</span>
        <button onClick={() => setPrivacy(true)} data-testid="landing-privacy-link" className="hover:text-white underline underline-offset-4 transition-colors">Privacy notice</button>
      </footer>
      <PrivacyDialog open={privacy} onOpenChange={setPrivacy} />
    </div>
  );
}
